export const RANGE_OPTIONS = [
  { value: 'past_week', label: 'Past week' },
  { value: 'past_month', label: 'Past month' },
  { value: 'past_3_months', label: 'Past 3 months' },
  { value: 'past_6_months', label: 'Past 6 months' },
  { value: 'past_year', label: 'Past year' },
  { value: 'all_time', label: 'All Time' },
];

export const DEFAULT_RANGE = 'past_6_months';

const FILTERS_STORAGE_KEY = 'controlCenterFilters';

const VALID_RANGES = new Set([...RANGE_OPTIONS.map((o) => o.value), 'custom']);

const DEFAULT_FILTERS = {
  range: DEFAULT_RANGE,
  customDateStart: null,
  customDateEnd: null,
  selectedClients: [],
};

export const getControlCenterFiltersStorageKey = (companyId) => {
  if (companyId == null || companyId === '') return null;
  return `${FILTERS_STORAGE_KEY}:${companyId}`;
};

/** One-time: move legacy unscoped key into the first company bucket that loads it. */
const migrateLegacyFiltersIfNeeded = (companyId) => {
  const key = getControlCenterFiltersStorageKey(companyId);
  if (!key || typeof window === 'undefined') return;
  try {
    if (localStorage.getItem(key)) return;
    const legacy = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!legacy) return;
    localStorage.setItem(key, legacy);
    localStorage.removeItem(FILTERS_STORAGE_KEY);
  } catch {
    // ignore quota / private mode
  }
};

const normalizeClients = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => item && item.id != null)
    .map((item) => ({
      id: item.id,
      client_name: typeof item.client_name === 'string' ? item.client_name : 'Unknown',
    }));
};

export const loadControlCenterFilters = (companyId) => {
  try {
    if (!companyId) return { ...DEFAULT_FILTERS };
    migrateLegacyFiltersIfNeeded(companyId);
    const raw = localStorage.getItem(getControlCenterFiltersStorageKey(companyId));
    if (!raw) return { ...DEFAULT_FILTERS };
    const parsed = JSON.parse(raw);
    return {
      range: VALID_RANGES.has(parsed.range) ? parsed.range : DEFAULT_RANGE,
      customDateStart: parsed.customDateStart || null,
      customDateEnd: parsed.customDateEnd || null,
      selectedClients: normalizeClients(parsed.selectedClients),
    };
  } catch {
    return { ...DEFAULT_FILTERS };
  }
};

export const saveControlCenterFilters = (companyId, filters) => {
  const key = getControlCenterFiltersStorageKey(companyId);
  if (!key) return;
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        range: filters.range || DEFAULT_RANGE,
        customDateStart: filters.customDateStart || null,
        customDateEnd: filters.customDateEnd || null,
        selectedClients: normalizeClients(filters.selectedClients),
      }),
    );
  } catch {
    // ignore quota / private mode
  }
};

const formatRangeDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US');
};

const UNBOUNDED_FUTURE = new Date(Date.parse('9999-12-31T23:59:59.999Z'));
const UNBOUNDED_PAST = new Date(Date.parse('1970-01-01T00:00:00.000Z'));

/**
 * Resolve the [dateFrom, dateTo] window for a Control Center range.
 * Mirrors contracting-api `resolveRange` so UI labels match API filters.
 * Custom: start-only → open end (unbounded future); end-only → open start (unbounded past).
 */
export const resolveControlCenterRange = (range = DEFAULT_RANGE, custom = {}) => {
  const normalized = VALID_RANGES.has(range) ? range : DEFAULT_RANGE;

  if (normalized === 'custom') {
    const customFrom = custom.dateFrom || custom.customDateStart
      ? new Date(custom.dateFrom || custom.customDateStart)
      : null;
    const customTo = custom.dateTo || custom.customDateEnd
      ? new Date(custom.dateTo || custom.customDateEnd)
      : null;
    const fromValid = customFrom && !Number.isNaN(customFrom.getTime());
    const toValid = customTo && !Number.isNaN(customTo.getTime());
    if (fromValid || toValid) {
      let dateFrom;
      let dateTo;
      if (fromValid) {
        dateFrom = customFrom;
        dateFrom.setHours(0, 0, 0, 0);
      } else {
        dateFrom = new Date(UNBOUNDED_PAST.getTime());
      }
      if (toValid) {
        dateTo = customTo;
        dateTo.setHours(23, 59, 59, 999);
      } else {
        dateTo = new Date(UNBOUNDED_FUTURE.getTime());
      }
      if (dateFrom.getTime() > dateTo.getTime()) {
        const swappedFrom = dateTo;
        const swappedTo = dateFrom;
        dateFrom = swappedFrom;
        dateTo = swappedTo;
        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 999);
      }
      return { dateFrom, dateTo, range: normalized };
    }
  }

  const dateTo = new Date();
  dateTo.setHours(23, 59, 59, 999);
  const dateFrom = new Date();
  dateFrom.setHours(0, 0, 0, 0);

  switch (normalized) {
    case 'past_week':
      dateFrom.setDate(dateFrom.getDate() - 7);
      break;
    case 'past_month':
      dateFrom.setMonth(dateFrom.getMonth() - 1);
      break;
    case 'past_3_months':
      dateFrom.setMonth(dateFrom.getMonth() - 3);
      break;
    case 'past_year':
      dateFrom.setFullYear(dateFrom.getFullYear() - 1);
      break;
    case 'all_time':
      // Fixed UTC epoch — mutate in place; avoid setHours(), which shifts by local timezone.
      dateFrom.setTime(Date.parse('1970-01-01T00:00:00.000Z'));
      break;
    case 'past_6_months':
    default:
      dateFrom.setMonth(dateFrom.getMonth() - 6);
      break;
  }

  return {
    dateFrom,
    dateTo,
    range: normalized === 'custom' ? DEFAULT_RANGE : normalized,
  };
};

export const getRangeLabel = (range, customDateStart, customDateEnd) => {
  if (range === 'all_time') return 'All Time';

  if (range === 'custom') {
    const start = customDateStart ? formatRangeDate(customDateStart) : '';
    const end = customDateEnd ? formatRangeDate(customDateEnd) : '';
    if (start && end) return `${start} - ${end}`;
    if (start) return `From ${start}`;
    if (end) return `Until ${end}`;
    return 'Custom range';
  }

  const baseLabel = RANGE_OPTIONS.find((opt) => opt.value === range)?.label || 'Past 6 months';
  const { dateFrom, dateTo } = resolveControlCenterRange(range);
  const start = formatRangeDate(dateFrom);
  const end = formatRangeDate(dateTo);
  if (!start || !end) return baseLabel;

  return `${baseLabel} / ${start} - ${end}`;
};

export const formatCurrency = (amount, { fractionDigits = 2 } = {}) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(Number(amount) || 0);

export const formatCurrencyCompact = (amount) =>
  formatCurrency(amount, { fractionDigits: Number.isInteger(Number(amount)) ? 0 : 2 });

export const panelSurfaceSx = (theme) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: isDark ? '#334155' : '#e7e7e7',
    borderRadius: '10px',
    overflow: 'hidden',
  };
};

export const getTechnicianDisplayName = (tech) => {
  if (!tech) return '';
  if (tech.displayName) return tech.displayName;
  if (tech.type === 'vendor') return tech.companyName || tech.company_name || '';
  return `${tech.firstName || tech.first_name || ''} ${tech.lastName || tech.last_name || ''}`.trim();
};
